import { PAYMENTMODE } from "@golf-district/shared";

/**
 * Maps a payment mode nid to the GA payment_mode value the client expects:
 * PRE_PAID → "prepaid", NO_PAY → "pay_at_course", PRE_AUTH → "pre_auth".
 */
export const paymentModeLabel = (nid?: number | null): string | undefined => {
  switch (nid) {
    case PAYMENTMODE.PRE_PAID:
      return "prepaid";
    case PAYMENTMODE.NO_PAY:
      return "pay_at_course";
    case PAYMENTMODE.PRE_AUTH:
      return "pre_auth";
    default:
      return undefined;
  }
};

type GoogleAnalyticsEvent = {
  action: string;
  category?: string;
  label?: string;
  value?: string | number;
  // Extra GA4-style event parameters (e.g. item_id, content_type, method).
  params?: Record<string, unknown>;
};

type CourseGAContext = {
  courseId?: string;
  courseName?: string;
  measurementId?: string | null;
  // Mirror of the course's GA_SHOW_ENHANCED_EVENTS setting. When false (the
  // default), custom/enhanced events are NOT delivered to this course's own GA
  // property — only to the master property. Default events (page_view + gtag.js
  // auto-events) go through a different path and are unaffected.
  showEnhancedEvents?: boolean;
};

// GA4 standard events that count as "default" even though they are dispatched
// through googleAnalyticsEvent(). These always reach the course property,
// regardless of GA_SHOW_ENHANCED_EVENTS. Everything else fired through this
// helper (btn_*, flt_*, etc.) is treated as an enhanced event.
const DEFAULT_EVENT_ACTIONS = new Set<string>([
  "page_view",
  "begin_checkout",
  "purchase",
]);

type EntityGAContext = {
  entityId?: string;
  entityName?: string | null;
};

const MASTER_ID = process.env.NEXT_PUBLIC_MEASUREMENT_ID;

// Pushes a gtag() command onto the GA dataLayer (the same path the GA snippet
// uses). next/script does not reliably expose a global gtag(), so we push
// directly. The gtag.js library only processes the arguments object (not a
// plain array), so we reconstruct that exact shape via an inner function — the
// same pattern the page-view tracker uses.
const gtag = (...args: unknown[]) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  function push(this: unknown) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  }
  // @ts-expect-error mirrors Google's variadic gtag(...) signature
  push(...args);
};

// Current course, kept in sync by <CourseGAContextSync> while a course page is
// mounted. This lets plain (non-hook) event helpers attach course_id /
// course_name and scope send_to without threading React context through every
// call site. Reset to {} when no course is mounted (e.g. the facility page).
let courseGAContext: CourseGAContext = {};

// Current facility/entity, set once by <EntityGAContextSync> at the root and
// kept for the whole session (the entity is fixed per domain). Lets every event
// carry entity_id / entity_name, including on the facility page where there is
// no course.
let entityGAContext: EntityGAContext = {};

// NOTE: GA config defaults (course_id/course_name/entity on EVERY hit, incl.
// gtag.js auto-events) are set deterministically elsewhere — by CourseGAConfig
// (server-rendered, before gtag.js loads, for the first paint) and by
// GAPageViewTracker (re-asserted on every navigation). These setters only keep
// the module context current so googleAnalyticsEvent() can attach per-event
// params; doing config writes from here (on mount/unmount) raced and cleared
// the long-lived master tag's course.
export const setCourseGAContext = (ctx: CourseGAContext) => {
  courseGAContext = ctx;
};

export const setEntityGAContext = (ctx: EntityGAContext) => {
  entityGAContext = ctx;
};

declare global {
  interface Window {
    gtag: (
      event: string,
      action: string,
      params: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
  }
}

export const googleAnalyticsEvent = ({
  action,
  category,
  label,
  value,
  params,
}: GoogleAnalyticsEvent) => {
  if (typeof window === "undefined") return;

  const { courseId, courseName, measurementId, showEnhancedEvents } =
    courseGAContext;
  const { entityId, entityName } = entityGAContext;
  // Only deliver an enhanced (custom) event to the course's own property when
  // the course has opted in via GA_SHOW_ENHANCED_EVENTS. Default events
  // (page_view, begin_checkout, purchase) always reach the course property. The
  // master property receives every event either way. This filtering happens
  // before dispatch, so a withheld event never touches the course property.
  const isEnhancedEvent = !DEFAULT_EVENT_ACTIONS.has(action);
  const includeCourse =
    Boolean(measurementId) && (!isEnhancedEvent || Boolean(showEnhancedEvents));

  // Build the list of GA properties this event should reach. The master
  // property always receives every event; the course's own property receives
  // an enhanced event only when the course opted in (GA_SHOW_ENHANCED_EVENTS).
  const targets: string[] = [];
  if (MASTER_ID) targets.push(MASTER_ID);
  if (includeCourse && measurementId) targets.push(measurementId);

  const baseParams: Record<string, unknown> = {
    ...(entityId ? { entity_id: entityId } : {}),
    ...(entityName ? { entity_name: entityName } : {}),
    ...(courseId ? { course_id: courseId } : {}),
    ...(courseName ? { course_name: courseName } : {}),
    ...(category !== undefined ? { event_category: category } : {}),
    ...(label !== undefined ? { event_label: label } : {}),
    ...(value !== undefined ? { value } : {}),
    ...params,
  };

  // Dispatch ONE hit per destination with a SINGLE-STRING send_to. gtag.js
  // honors a string send_to to route an event to exactly one property, but an
  // ARRAY send_to is not reliably honored for EXCLUSION — with an array, a
  // configured course property still receives events "scoped" to the master
  // only. Per-destination dispatch guarantees an excluded course property never
  // receives the event. Sending via the shared dataLayer pusher (same path as
  // the page-view tracker); window.gtag is not reliably exposed by next/script.
  // No target means there is no property that should receive this event (e.g.
  // master unset AND the course opted out of enhanced events). Drop it rather
  // than firing an untargeted hit, which would broadcast to every configured
  // property — including the course we are trying to exclude.
  targets.forEach((target) => {
    gtag("event", action, { ...baseParams, send_to: target });
  });
};
